import React, { useState, useRef, useEffect } from 'react';
import { FaRobot, FaPaperPlane, FaUser, FaTimes } from 'react-icons/fa';

const ChatAssistant = ({ onClose, language = 'english' }) => {
    const [messages, setMessages] = useState([
        { text: "Welcome to NewranSS! 🌸 I'm here to guide you through our platform and ensure your experience is as smooth as possible. How can I help you today?", isBot: true }
    ]);
    const [input, setInput] = useState('');
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    
    // Enhanced knowledge base for the chatbot with more detailed information and keywords
  const knowledgeBase = {

    support_services: {
        response: "We provide a wide range of disability support services under the NDIS, tailored to each participant’s individual needs and goals. This includes personal care, assistance with daily activities, therapy support, social participation, and supported independent living.",
        keywords: ["support", "services", "ndis support", "care", "daily living", "community", "assistance", "personal care"]
    },
    find_services: {
        response: "You can explore our support services based on your goals, location, and support category. We offer detailed descriptions, provider profiles, and transparent service options to help you find the best fit.",
        keywords: ["browse", "find", "explore", "search", "services", "support options", "locations"]
    },
    communication: {
        response: "Stay connected with our team through secure messaging, scheduled check-ins, or direct phone calls. Your assigned care coordinator will ensure you're supported and updated at every step.",
        keywords: ["contact", "message", "communicate", "talk", "coordinator", "support team", "call", "inquiry"]
    },
    payments: {
        response: "Our system helps you manage NDIS-related payments with ease. View service bookings, monitor your plan budgets, access invoices, and receive timely payment alerts — all in one place.",
        keywords: ["payments", "bill", "invoice", "ndis funding", "finance", "payment history", "budget", "fee"]
    },
    feedback: {
        response: "We value your feedback. Share your experience, concerns, or complaints through our dedicated feedback channel. Every submission is reviewed with professionalism and aligned with NDIS quality standards.",
        keywords: ["feedback", "complaint", "report", "issue", "concern", "suggestion", "support issue", "problem"]
    },
    language: {
        response: "We offer services in multiple languages to support Australia's diverse community. You can select your preferred language or request an interpreter to assist during appointments or support sessions.",
        keywords: ["language", "translation", "interpreter", "multi-language", "accessibility", "english", "cultural support"]
    },
    access: {
        response: "Log in or register to access your personalised NDIS dashboard. Manage your support plan, view appointments, message your providers, and track your progress securely and easily.",
        keywords: ["login", "sign in", "register", "account", "ndis access", "portal", "secure login", "sign up"]
    },
    providers_portal: {
        response: "NDIS providers and support coordinators can manage participant plans, track service delivery, submit reports, and collaborate via our secure provider portal.",
        keywords: ["provider", "dashboard", "coordinator", "portal", "manage services", "client records", "staff portal"]
    },
    service_registration: {
        response: "If you're a registered provider, you can submit new support services through our Provider Dashboard. Include service details, pricing, and credentials for approval and listing.",
        keywords: ["add service", "register", "new service", "create listing", "therapy registration", "provider service"]
    },
    documentation: {
        response: "Participants and providers can upload and manage key documents like service agreements, care plans, reports, and progress notes. All documents are stored securely and accessible on demand.",
        keywords: ["documents", "records", "agreements", "reports", "care plan", "upload", "files", "progress notes"]
    },
    incident_tracking: {
        response: "Our incident tracking system ensures transparency and compliance. Log any incidents or concerns, monitor resolution progress, and stay updated on outcomes in real time.",
        keywords: ["incident", "issue tracking", "complaint", "report", "case management", "resolution", "monitoring"]
    },
    plan_management: {
        response: "Whether your plan is self-managed, plan-managed, or NDIA-managed, our platform allows you to monitor budgets, track service usage, and adjust spending — helping you get the most from your NDIS plan.",
        keywords: ["plan", "funding", "budget", "rates", "pricing", "ndis management", "usage", "allocation"]
    },
    transport_assistance: {
        response: "Transport support is available to help participants attend medical appointments, community activities, or day programs. We can arrange travel through support workers or trusted transport partners.",
        keywords: ["transport", "travel", "appointment transport", "car", "community access", "mobility", "ride", "shuttle"]
    },
    safety: {
        response: "Your safety is our top priority. All team members are screened, qualified, and trained to deliver care that is safe, respectful, and in line with NDIS requirements. We also follow strict data privacy protocols.",
        keywords: ["safety", "security", "safe", "care", "protection", "worker screening", "compliance", "trust"]
    },
    reviews: {
        response: "We welcome your reviews. Share your feedback to help others make informed decisions and assist us in improving our services. Participant voices help shape a better support experience.",
        keywords: ["review", "feedback", "experience", "testimonial", "rating", "recommend", "stars"]
    },
    cancellation_policy: {
        response: "You can cancel or reschedule services in accordance with the NDIS cancellation policy. Check your dashboard or provider agreement for notice periods and any applicable fees.",
        keywords: ["cancel", "reschedule", "change booking", "cancellation policy", "ndis cancellation", "refund"]
    }
}
    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Focus input when chat opens
    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    // Enhanced query processing with AI-like capabilities
    const processQuery = (query) => {
        const lowerQuery = query.toLowerCase();
        
        // Check for greetings and general inquiries first
        if (containsAny(lowerQuery, ["hello", "hi", "hey", "greetings", "good morning", "good afternoon", "good evening"])) {
            return "Hello! Welcome to NewranSS. How can I assist you with your needs today?";
        }
        
        if (containsAny(lowerQuery, ["what can you do", "help me", "how does this work", "about", "features", "what is NewranSS"])) {
            return "**NewranSS** is your all-in-one platform! I can help you with browsing properties, booking rooms, managing payments, communicating with property owners, reporting issues, and more. What specific aspect would you like to explore?";
        }
        
        if (containsAny(lowerQuery, ["thank", "thanks", "appreciate", "helpful"])) {
            return "You're welcome! I'm happy to assist. Is there anything else you'd like to know about NewranSS?";
        }
        
        // Search for the most relevant response
        let bestMatch = findBestMatch(lowerQuery);
        
        // If no good match found, provide a helpful general response
       if (!bestMatch) {
    return "I’m sorry, I didn’t quite catch that. Could you please rephrase your question? I can help you with NDIS support services, plan management, transport, payments, and more.";
}
        
        return bestMatch;
    };

    // Helper function to check if the query contains any keywords from an array
    const containsAny = (text, keywords) => {
        return keywords.some(keyword => text.includes(keyword));
    };
    
    // Enhanced function to find the best match from the knowledge base
    const findBestMatch = (query) => {
        let bestResponse = null;
        let highestScore = 0;
        
        // Calculate relevance score for each topic
        Object.entries(knowledgeBase).forEach(([topic, data]) => {
            let score = 0;
            
            // Check direct keyword matches
            data.keywords.forEach(keyword => {
                if (query.includes(keyword)) {
                    // Single word keywords get lower weight
                    const weight = keyword.split(' ').length > 1 ? 2 : 1;
                    score += weight;
                    
                    // Boost score for exact phrase matches
                    if (query.includes(` ${keyword} `)) {
                        score += 1;
                    }
                }
            });
            
            // Process context-specific queries
            if (topic === 'booking' && containsAny(query, ['how to book', 'how do i book', 'want to book', 'interested in booking'])) {
                score += 3;
            } else if (topic === 'login' && containsAny(query, ['cant login', 'trouble logging in', 'forgot password', 'create account'])) {
                score += 3;
            } else if (topic === 'cancellation' && containsAny(query, ['need to cancel', 'want to cancel', 'refund policy', 'change my booking'])) {
                score += 3;
            }
            
            // Check for question patterns
            if (query.startsWith('how') || query.startsWith('what') || query.startsWith('where') || 
                query.startsWith('can i') || query.startsWith('do you') || query.includes('?')) {
                // More specific matches for questions
                if ((topic === 'booking' && containsAny(query, ['how book', 'how to book', 'how can i book'])) ||
                    (topic === 'browse' && containsAny(query, ['how find', 'how search', 'how to find'])) ||
                    (topic === 'complaints' && containsAny(query, ['how report', 'how to report', 'how complain'])) ||
                    (topic === 'login' && containsAny(query, ['how login', 'how to login', 'how sign up', 'how create account']))) {
                    score += 2;
                }
            }
            
            // Update best match if this topic has a higher score
            if (score > highestScore) {
                highestScore = score;
                bestResponse = data.response;
            }
        });
        
        // Return the best matching response or null if no good match
        return highestScore > 0 ? bestResponse : null;
    };

    const handleSendMessage = (e) => {
        e.preventDefault();
        if (!input.trim()) return;

        // Add user message
        const userMessage = { text: input, isBot: false };
        setMessages([...messages, userMessage]);
        setInput('');

        // Process and respond with realistic typing delay
        setTimeout(() => {
            const response = processQuery(input);
            setMessages(prevMessages => [...prevMessages, { text: response, isBot: true }]);
        }, Math.min(600 + response.length * 10, 1500)); // Dynamic delay based on response length
    };
    
    // Get a personalized response based on query length for more natural feel
    const response = (input) => {
        const processed = processQuery(input);
        return processed;
    };

    return (
        <div className="chatbot-container">
            <div className="chatbot-popup">
                <div className="chatbot-header">
                    <div className="d-flex align-items-center">
                        <FaRobot className="me-2" size={20} />
                        <h5 className="m-0" style={{color : 'white'}}>NewranSS Assistant</h5>
                    </div>
                    <button className="close-button" onClick={onClose}>
                        <FaTimes size={16} />
                    </button>
                </div>
                <div className="chatbot-messages">
                    {messages.map((msg, index) => (
                        <div 
                            key={index} 
                            className={`message-container ${msg.isBot ? 'bot-message' : 'user-message'}`}
                        >
                            <div className="message-bubble">
                                <div className="message-sender">
                                    {msg.isBot ? 
                                        <><FaRobot className="me-1" size={12} /> <small>NewranSS Assistant</small></> :
                                        <><small>You</small> <FaUser className="ms-1" size={12} /></>
                                    }
                                </div>
                                <div className="message-text">{msg.text}</div>
                            </div>
                        </div>
                    ))}
                    <div ref={messagesEndRef} />
                </div>
                <form className="chatbot-input" onSubmit={handleSendMessage}>
                    <input
                        ref={inputRef}
                        type="text"
                        placeholder="Type your question here..."
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                    />
                    <button type="submit" disabled={!input.trim()}>
                        <FaPaperPlane />
                    </button>
                </form>
            </div>
            
            <style jsx>{`
                .chatbot-container {
                    position: fixed;
                    bottom: 20px;
                    right: 20px;
                    z-index: 1000;
                }
                
                .chatbot-popup {
                    width: 350px;
                    height: 450px;
                    background-color: white;
                    border-radius: 12px;
                    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                }
                
                .chatbot-header {
                    background: linear-gradient(135deg,rgb(195, 74, 255) 0%,rgb(160, 43, 111) 100%);
                    color: white;
                    padding: 15px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }
                
                .close-button {
                    background: transparent;
                    border: none;
                    color: white;
                    cursor: pointer;
                    width: 30px;
                    height: 30px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 50%;
                    transition: background-color 0.3s;
                }
                
                .close-button:hover {
                    background-color: rgba(255, 255, 255, 0.2);
                }
                
                .chatbot-messages {
                    flex: 1;
                    padding: 15px;
                    overflow-y: auto;
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                }
                
                .message-container {
                    display: flex;
                    margin-bottom: 10px;
                }
                
                .bot-message {
                    justify-content: flex-start;
                }
                
                .user-message {
                    justify-content: flex-end;
                }
                
                .message-bubble {
                    max-width: 80%;
                    padding: 10px 12px;
                    border-radius: 12px;
                    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
                }
                
                .bot-message .message-bubble {
                    background-color: #f0f2f5;
                    color: #333;
                    border-bottom-left-radius: 4px;
                }
                
                .user-message .message-bubble {
                    background-color: #4a6bff;
                    color: white;
                    border-bottom-right-radius: 4px;
                }
                
                .message-sender {
                    display: flex;
                    align-items: center;
                    font-size: 12px;
                    margin-bottom: 4px;
                    opacity: 0.8;
                }
                
                .message-text {
                    line-height: 1.4;
                    white-space: pre-wrap;
                }
                
                .chatbot-input {
                    display: flex;
                    padding: 10px;
                    border-top: 1px solid #eaeaea;
                    background-color: #f8f9fa;
                }
                
                .chatbot-input input {
                    flex: 1;
                    padding: 10px 15px;
                    border: 1px solid #ddd;
                    border-radius: 20px;
                    outline: none;
                    transition: border-color 0.3s;
                }
                
                .chatbot-input input:focus {
                    border-color: #4a6bff;
                }
                
                .chatbot-input button {
                    margin-left: 10px;
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    background-color: #4a6bff;
                    color: white;
                    border: none;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: background-color 0.3s;
                }
                
                .chatbot-input button:hover {
                    background-color: #3a55cc;
                }
                
                .chatbot-input button:disabled {
                    background-color: #b4c0ff;
                    cursor: not-allowed;
                }
                
                @media (max-width: 480px) {
                    .chatbot-popup {
                        width: 300px;
                        height: 400px;
                        bottom: 0;
                        right: 0;
                    }
                }
            `}</style>
        </div>
    );
};

export default ChatAssistant;